import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { createPollingInterval } from '@/utils/realtimeOptimizer';

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  message_type: 'text' | 'voice' | 'image' | 'file' | 'ai_response';
  is_read: boolean;
  is_delivered: boolean;
  is_ai_generated: boolean;
  ai_model?: string;
  metadata?: any;
  parent_message_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Conversation {
  id: string;
  participant_1_id: string;
  participant_2_id: string;
  conversation_type: string;
  is_archived: boolean;
  is_muted: boolean;
  total_messages: number;
  unread_count_participant_1: number;
  unread_count_participant_2: number;
  last_message_at: string;
  created_at: string;
  updated_at: string;
}

export interface TypingIndicator {
  id: string;
  conversation_id: string;
  user_id: string;
  is_typing: boolean;
  last_activity: string;
}

export const useChat = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<{ [conversationId: string]: ChatMessage[] }>({});
  const [typingIndicators, setTypingIndicators] = useState<{ [conversationId: string]: TypingIndicator[] }>({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [user]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId: string, forceRefresh = false) => {
    // Skip if already loaded and not forcing refresh
    if (!forceRefresh && messages[conversationId]?.length > 0) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      setMessages(prev => ({
        ...prev,
        [conversationId]: (data || []).map(msg => ({
          ...msg,
          message_type: msg.message_type as ChatMessage['message_type']
        }))
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [messages]);

  // Send a message
  const sendMessage = useCallback(async (
    conversationId: string, 
    content: string, 
    recipientId: string,
    messageType: 'text' | 'voice' | 'image' | 'file' | 'ai_response' = 'text'
  ) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          recipient_id: recipientId,
          content,
          message_type: messageType
        })
        .select()
        .single();

      if (error) throw error;
      
      // Optimistic update: Add message to local state immediately
      if (data) {
        const chatMessage: ChatMessage = {
          id: data.id,
          conversation_id: data.conversation_id,
          sender_id: data.sender_id,
          recipient_id: data.recipient_id,
          content: data.content,
          message_type: data.message_type as ChatMessage['message_type'],
          is_read: data.is_read || false,
          is_delivered: data.is_delivered || false,
          is_ai_generated: data.is_ai_generated || false,
          ai_model: data.ai_model,
          metadata: data.metadata,
          parent_message_id: data.parent_message_id,
          created_at: data.created_at,
          updated_at: data.updated_at
        };
        
        setMessages(prev => ({
          ...prev,
          [conversationId]: [
            ...(prev[conversationId] || []),
            chatMessage
          ].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        }));
        
        // 📧 Envoyer email de notification pour les messages normaux (non-bloquant)
        if (messageType !== 'ai_response') {
          supabase.functions.invoke('email-new-message', {
            body: {
              senderId: user.id,
              recipientId: recipientId,
              messageId: data.id,
              conversationId: conversationId,
              content: content.substring(0, 300) // Limiter la taille du preview
            }
          }).then(res => {
            if (res.error) {
              console.warn('Failed to send new message email:', res.error);
            }
          }).catch(err => {
            console.warn('Email function error:', err);
          });
        }
      }
      
      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [user]);

  // Send AI message
  const sendAIMessage = useCallback(async (
    conversationId: string,
    userMessage: string,
    recipientId: string
  ) => {
    if (!user) return;

    try {
      // Get conversation history for context
      const conversationMessages = messages[conversationId] || [];
      const conversation_history = conversationMessages.slice(-10).map(msg => ({
        role: msg.sender_id === user.id ? 'user' : 'assistant',
        content: msg.content
      }));

      // Get user context
      const { data: member } = await supabase
        .from('club_members')
        .select('name, company, role, bio, skills, industry')
        .eq('user_id', user.id)
        .maybeSingle();

      const user_context = {
        name: member?.name,
        company: member?.company,
        role: member?.role,
        bio: member?.bio,
        skills: member?.skills,
        industry: member?.industry
      };

      // Call AI function
      const { data: aiData, error: aiError } = await supabase.functions.invoke('chat-ai', {
        body: {
          message: userMessage,
          conversation_history,
          user_context
        }
      });

      if (aiError) throw aiError;

      // Send AI response as message (sendMessage already handles optimistic update)
      const aiMessage = await sendMessage(
        conversationId,
        aiData.response || 'Désolé, je ne peux pas répondre maintenant.',
        recipientId,
        'ai_response'
      );

      return aiMessage;
    } catch (error) {
      console.error('Error sending AI message:', error);
      // Send fallback message
      const fallbackMessage = await sendMessage(
        conversationId,
        'Assistant IA temporairement indisponible. Veuillez réessayer plus tard.',
        recipientId,
        'ai_response'
      );
      return fallbackMessage;
    }
  }, [user, messages, sendMessage]);

  // Create or get conversation
  const getOrCreateConversation = useCallback(async (otherUserId: string) => {
    if (!user) return null;

    try {
      // Check if conversation already exists
      const { data: existing } = await supabase
        .from('conversations')
        .select('*')
        .or(`and(participant_1_id.eq.${user.id},participant_2_id.eq.${otherUserId}),and(participant_1_id.eq.${otherUserId},participant_2_id.eq.${user.id})`)
        .maybeSingle();

      if (existing) {
        return existing;
      }

      // Create new conversation
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          participant_1_id: user.id < otherUserId ? user.id : otherUserId,
          participant_2_id: user.id < otherUserId ? otherUserId : user.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [user]);

  // Update typing indicator
  const updateTyping = useCallback(async (conversationId: string, isTyping: boolean) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('typing_indicators')
        .upsert({
          conversation_id: conversationId,
          user_id: user.id,
          is_typing: isTyping,
          last_activity: new Date().toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating typing indicator:', error);
    }
  }, [user]);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [user]);

  // Setup realtime subscriptions
  useEffect(() => {
    if (!user) return;

    // 🔥 PHASE 1: Utiliser polling pour conversations (pas critique)
    const pollingCleanup = createPollingInterval('conversations', fetchConversations, true);

    // ✅ Garder Realtime uniquement pour messages (table critique)
    const messagesSubscription = supabase
      .channel('messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages'
      }, (payload) => {
        if (payload.new) {
          const rawMessage = payload.new as any;
          
          if (rawMessage && (rawMessage.sender_id === user.id || rawMessage.recipient_id === user.id)) {
            const chatMessage: ChatMessage = {
              id: rawMessage.id,
              conversation_id: rawMessage.conversation_id,
              sender_id: rawMessage.sender_id,
              recipient_id: rawMessage.recipient_id,
              content: rawMessage.content,
              message_type: rawMessage.message_type as ChatMessage['message_type'],
              is_read: rawMessage.is_read,
              is_delivered: rawMessage.is_delivered,
              is_ai_generated: rawMessage.is_ai_generated,
              ai_model: rawMessage.ai_model,
              metadata: rawMessage.metadata,
              parent_message_id: rawMessage.parent_message_id,
              created_at: rawMessage.created_at,
              updated_at: rawMessage.updated_at
            };
            
            // Prevent duplicates from optimistic updates
            setMessages(prev => {
              const existing = (prev[chatMessage.conversation_id] || []).find(m => m.id === chatMessage.id);
              if (existing) {
                return prev;
              }
              
              return {
                ...prev,
                [chatMessage.conversation_id]: [
                  ...(prev[chatMessage.conversation_id] || []),
                  chatMessage
                ].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
              };
            });
          }
        }
      })
      .subscribe();

    // ✅ Garder Realtime pour typing indicators (table critique)
    const typingSubscription = supabase
      .channel('typing')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'typing_indicators'
      }, (payload) => {
        if (payload.new) {
          const indicator = payload.new as TypingIndicator;
          
          if (indicator) {
            setTypingIndicators(prev => ({
              ...prev,
              [indicator.conversation_id]: [
                ...(prev[indicator.conversation_id] || []).filter(i => i.user_id !== indicator.user_id),
                indicator
              ].filter(i => i.is_typing)
            }));
          }
        }
      })
      .subscribe();

    return () => {
      pollingCleanup();
      messagesSubscription.unsubscribe();
      typingSubscription.unsubscribe();
    };
  }, [user, fetchConversations]);

  // Initial data load
  useEffect(() => {
    if (user) {
      fetchConversations().finally(() => setIsLoading(false));
    }
  }, [user, fetchConversations]);

  return {
    conversations,
    messages,
    typingIndicators,
    isLoading,
    sendMessage,
    sendAIMessage,
    getOrCreateConversation,
    updateTyping,
    markAsRead,
    fetchMessages
  };
};